import { APIGet, APIPost, APIPut, APIDelete, APIUrl, AdminType } from "api/protocols";
import { CancelToken } from "axios";
import { IProduct, IProductImage, IVariant } from "redux/reducers/models/product";

export type TExtendedProductFilters = {
  /** Return all the products that have these product types. */
  type: string[];
  /** Return all the products from the department with these ID's. */
  department_id: number[];
  /** Return all the products from the department categories with these ID's. */
  category_id: number[];
  /** Return all the products from the subcategories with these ID's. */
  subcategory_id: number[];
  // Return all vendors
  vendor_id: number[];
};

export type TProductMetaData = {
  membership_type?: string;
  /** Default = false */
  reciprocal?: boolean;
  expiry?: Date | string;
  expiry_type?: string;
  customer_type_id?: number | null;
  button_color?: string;
};

export type TGetProduct = {
  all?: boolean;
  id?: string | number;
  ids?: Array<number>;
  search?: string;
  filters?: {
    label: keyof TExtendedProductFilters | "uncategorized";
    value: string[] | number[];
  }[];
  type?: string;
  types?: string[];
  track_inventory?: boolean;
  folder_id?: string | number;
  variants?: boolean;
  /** Lightweight alternative to `inventory`: loads only each variant's facility inventory level. */
  variant_inventory?: boolean;
  /** Default "all". With "any", products matching any department, category or subcategory filter are returned. */
  department_match?: "all" | "any";
  sales_channel_type?: "online_store";
  /** This is a taxing call to make. */
  extended?: boolean;
  extended_variants?: boolean;
  inventory?: boolean;
  sales_channels?: boolean;
  modifier_groups?: boolean;
  group_memberships?: boolean;
  /** Default: 50 */
  limit?: number;
  /** Default: 0 */
  offset?: number;
  /** Default: 0 */
  archived?: 0 | 1;
  vendor_id?: number;
  skus?: Array<string>;
  warehouse?: boolean;
  /**Return the attached warehouse products */
  warehouse_products?: boolean;
  get_count?: boolean;
  sort_by?: "title" | "price" | "inventory";
  sort_direction?: "asc" | "desc";
};

export type TPostProduct = {
  title?: string;
  /** Default: null */
  subtitle?: string;
  /** Default: null */
  preferred_title?: string;
  type?: string;
  description?: string;
  internal_description?: string;
  vendor_id?: number;
  /** Default = 0 */
  customer_required?: boolean;
  /** Default = 0 */
  shipping_required?: boolean;
  /** Default = 0 */
  fulfillment_required?: boolean;
  /** Default = 0 */
  track_inventory?: boolean;
  /** Default = 0 */
  gift_card?: boolean;
  /** Default = null */
  available_on?: Date;

  available_on_time?: string;
  /** Default = null */
  unavailable_on?: Date;

  unavailable_on_time?: string;
  /** Default = facility */
  applicable_taxes?: "facility" | "customer";
  department_id?: number;
  category_id?: number;
  subcategory_id?: number;
  accounting_reference_id?: number;
  account_id?: number;
  price?: number;
  original_price?: number;
  cost?: number;
  /** Default = 1 */
  taxable?: boolean;
  barcode?: string | null;
  sku?: string | null;
  /** Default = 0 */
  tracked?: boolean;
  tax_line_ids?: number[];
  options?: { name: string; values: string[] }[];
  meta?: TProductMetaData;
  auto_generate_sku?: 0 | 1;
  product_unit_id?: number;
  warehouse?: boolean;
  gift_card_restrict_facility?: boolean;
  gift_card_expiry_type?: string;
  gift_card_expiry_years?: number;
  image?: IProductImage[];
  dynamic_combo?: boolean;
  dynamic_combo_quantity?: number;
  modifier_groups?: { modifier_group_id: number; required: boolean }[];
  dynamic_price?: boolean;
  tax_exempt?: boolean;
  variant_multiselect?: boolean;
};

export type TPutProduct = {
  id: string | number;
  title?: string;
  subtitle?: string;
  /** Default = null */
  preferred_title?: string;
  type?: string;
  description?: string;
  internal_description?: string;
  vendor_id?: number;
  customer_required?: boolean;
  shipping_required?: boolean;
  fulfillment_required?: boolean;
  track_inventory?: boolean;
  gift_card?: boolean;
  available_on?: Date | string; //Date string
  unavailable_on?: Date | string; // Date string
  applicable_taxes?: string; // "facility" | "customer"
  department_id?: number;
  category_id?: number;
  subcategory_id?: number;
  accounting_reference_id?: string | number;
  account_id?: number;
  price?: number;
  original_price?: number;
  cost?: number;
  barcode?: string | null;
  sku?: string | null;
  meta?: {
    kitchen_location_id?: number;
    kitchen_location_id_2?: number;
    kitchen_location_id_3?: number;
    button_color?: string;
    cannot_be_sold?: boolean;
    loyalty_points_override?: string;
  };
  taxable?: boolean;
  product_unit_id?: number;
  product_unit?: any;
  warehouse?: boolean;
  warehouse_product_id?: number;
  inventory_units?: number;
  gift_card_restrict_facility?: boolean;
  gift_card_expiry_type?: string;
  gift_card_expiry_years?: number;
  dynamic_combo?: boolean;
  dynamic_combo_quantity?: number;
  warehouse_product_ids?: Array<{ warehouse_product_id: number; inventory_units?: string }>;
  warehouse_products?: Array<IProduct & { inventory_units?: string; variant_id?: number }>;
  dynamic_price?: boolean;
  variant_multiselect?: boolean;
};

interface IProductColor {
  id: number;
  button_color: string;
}

interface IPutProductColor {
  products: IProductColor[];
}
interface IPutVariantLabels {
  offset: number;
  variants: Array<{ variant_id: number; quantity: number }>;
  template_id?: number;
  hide_barcode?: boolean;
}

/** filter params that can be added into the product API call. */
export type FacilityProductExtendedFilters = {
  /** Return all the products that have these product types. */
  type: string[];
  /** Return all the products from the department with these ID's. */
  department_id: number[];
  /** Return all the products from the department categories with these ID's. */
  category_id: number[];
  /** Return all the products from the subcategories with these ID's. */
  subcategory_id: number[];

  vendor_id: number[];
};

export type ExtendedFacilityProductFilterKey = keyof FacilityProductExtendedFilters;

// interface IGetProductRes {
//   data: IProduct[];
//   message: string;
//   status: StatusCode;
// }

export interface IVariantToAttach {
  variant_id: number;
  price?: number;
  cost?: number;
  quantity?: number;
}

export async function GetProduct(params: TGetProduct, useGlobalLoader: boolean, cancelToken?: CancelToken) {
  return await APIGet<IProduct[]>(APIUrl(AdminType.FacilityAdmin, "/product"), params, useGlobalLoader, cancelToken);
}

export async function PutVariantLabels(params: IPutVariantLabels, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/product/variant/label"), params, useGlobalLoader);
}

export async function PostProduct(params: TPostProduct, useGlobalLoader: boolean) {
  return await APIPost<IProduct>(APIUrl(AdminType.FacilityAdmin, "/product"), params, useGlobalLoader);
}

export async function PutProduct(params: TPutProduct, useGlobalLoader: boolean) {
  return await APIPut<IProduct>(APIUrl(AdminType.FacilityAdmin, "/product"), params, useGlobalLoader);
}
/** Deleting a product will only remove it from the facilities list. */
export async function DeleteProduct(params: { id: string | number }, useGlobalLoader: boolean) {
  return await APIDelete(APIUrl(AdminType.FacilityAdmin, "/product"), params, useGlobalLoader);
}

// Attach a product to a facility
export async function PutProductAttach(params: { product_id: string | number }, useGlobalLoader?: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/product/attach"), params, useGlobalLoader);
}

export async function PutProductDetach(params: { product_ids: Array<number> }, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/product/detach"), params, useGlobalLoader);
}

export async function PutProductArchive(params: { product_ids: Array<number> }, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/product/archive-multiple"), params, useGlobalLoader);
}

// Duplicate Product / Variant
export async function DuplicateProduct(params: { product_id: string | number }, useGlobalLoader: boolean) {
  return await APIPost<IProduct>(APIUrl(AdminType.FacilityAdmin, "/product/duplicate"), params, useGlobalLoader);
}

// Get Product Options
export async function GetProductOptions(
  params?: { product_id: string | number },
  useGlobalLoader?: boolean,
  cancelToken?: CancelToken,
) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/product/option"), params, useGlobalLoader, cancelToken);
}

// Post a product option
export async function PostProductOption(
  params: { product_id: string | number; name: string; values?: any[] },
  useGlobalLoader: boolean,
) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/product/option"), params, useGlobalLoader);
}

// POST Add Product to Variant.  Used with combo products
export async function AddProductsToVariant(
  params: any,
  //{ base_variant_id: number; variants_to_attach: IVariantToAttach[] },

  useGlobalLoader: boolean,
) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/product/combination"), params, useGlobalLoader);
}

/** Update a product combo price | cost */
export async function UpdateComboProduct(
  params: { base_variant_id: number; variants_to_attach: IVariantToAttach[] },
  useGlobalLoader: boolean,
) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/product/combination"), params, useGlobalLoader);
}

export function GetComboVariants(
  params: { base_variant_id: number },
  useGlobalLoader: boolean,
  cancelToken?: CancelToken,
) {
  return APIGet<IVariant>(
    APIUrl(AdminType.FacilityAdmin, "/product/combination/variants"),
    params,
    useGlobalLoader,
    cancelToken,
  );
}

export type TRemoveProductFromVariant = {
  /** the base product ID */
  base_variant_id: number;

  /** the variant IDs to remove from the base product */
  variant_id: number[];
};
// DELETE Products from Variant
export async function RemoveProductFromVariant(params: TRemoveProductFromVariant, useGlobalLoader: boolean) {
  return await APIDelete(APIUrl(AdminType.FacilityAdmin, "/product/combination"), params, useGlobalLoader);
}

export async function PutProductColor(params: IPutProductColor, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/product/color"), params, useGlobalLoader);
}

export async function DuplicateVariant(params: any, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/product/variant/duplicate"), params, useGlobalLoader);
}

export async function PutArchiveProduct(params: { id: number }, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/product/archive"), params, useGlobalLoader);
}
